<template>
    <base-dialog :loading="loading" :value="dialog" :width="460" :title="$t('common.filter_chat')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="fc-form">
            <select-option-students no-list :value="optionChat" @change="onTabChange" />

            <template v-if="!isTeamChats">
                <kimsa-text-field icon="mdi-magnify" dense :placeholder="`${$t('common.search')}`"
                    @change="search = $event" :value="search" force-value />
                <div class="fc-list">
                    <label class="fc-item" v-for="(item, i) in filteredOptions" :key="i">
                        <input type="checkbox" :checked="isChecked(item, option)" @change="toggle(item, option, $event)">
                        <span class="fc-item-name">{{ item.name }}</span>
                        <span v-if="optionChat === 'group'" class="fc-item-count">{{ (item.students || []).length }}</span>
                    </label>
                    <div v-if="!filteredOptions.length" class="fc-empty">{{ $t('common.not_found') }}</div>
                </div>
            </template>

            <template v-else>
                <div class="fc-field">
                    <span class="fc-sublabel">{{ $t('common.team') }}</span>
                    <kimsa-combobox :placeholder="$t('common.select_team')" dense :items="teams" @change="setTeam"
                        :value="team" />
                </div>
                <div class="fc-field">
                    <span class="fc-sublabel">{{ $t('common.subgroup') }}</span>
                    <kimsa-text-field class="mb-2" icon="mdi-magnify" dense :placeholder="`${$t('common.search')}`"
                        @change="subSearch = $event" :value="subSearch" force-value />
                    <div class="fc-list">
                        <label class="fc-item" v-for="(item, i) in filteredSubOptions" :key="i">
                            <input type="checkbox" :checked="isChecked(item, subOption)"
                                @change="toggle(item, subOption, $event)">
                            <span class="fc-item-name">{{ item.name }}</span>
                        </label>
                        <div v-if="!filteredSubOptions.length" class="fc-empty">{{ $t('common.not_found') }}</div>
                    </div>
                </div>
            </template>
        </div>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.apply_filter')" :cancel-label="$t('common.clean')"
                :loading="loading" @cancel="clearSelection" @confirm="filter" />
        </template>
    </base-dialog>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { mapMutations, mapState } from "vuex";
import { UserIndex } from "@/utils/Algolia";
import { db, Timestamp } from "@/utils/Firebase";
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import SelectOptionStudents from './SelectOptionStudents.vue';
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";

const MAX_SELECTED = 6

export default {
    name: "FilterChatDialog",
    components: { KimsaTextField, KimsaCombobox, SelectOptionStudents, BaseDialog, DialogActions },
    props: {
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
        chatType: {
            type: String,
            default: 'student'
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            options: [],
            option: [],
            subOption: [],
            team: null,
            optionChat: 'student',
            search: '',
            subSearch: '',
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        coachId(vm = this) {
            return vm.currentUser?.uid || null
        },
        isStudentChats(vm = this) {
            return vm.optionChat === 'student'
        },
        isTeamChats(vm = this) {
            return vm.optionChat === 'team'
        },
        filteredOptions(vm = this) {
            const term = vm.search.trim().toLowerCase()
            if (!term) return vm.options
            return vm.options.filter(op => op.name?.toLowerCase().includes(term))
        },
        subOptions(vm = this) {
            return vm.team?.groups?.map(group => ({
                id: group.id,
                name: group.name,
                teamId: vm.team.id,
                teamName: vm.team.name,
                students: group.students
            })) || []
        },
        filteredSubOptions(vm = this) {
            const term = vm.subSearch.trim().toLowerCase()
            if (!term) return vm.subOptions
            return vm.subOptions.filter(op => op.name?.toLowerCase().includes(term))
        }
    },
    methods: {
        show() {
            const vm = this
            vm.optionChat = vm.chatType || 'student'
            vm.dialog = true
            vm.loadOptions()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.option = []
            vm.subOption = []
            vm.team = null
            vm.search = ''
            vm.subSearch = ''
            vm.optionChat = 'student'
        },
        onTabChange(value) {
            const vm = this
            vm.optionChat = value
            vm.option = []
            vm.subOption = []
            vm.team = null
            vm.search = ''
            vm.subSearch = ''
            if (vm.dialog) vm.loadOptions()
        },
        async loadOptions() {
            const vm = this;
            if (vm.isStudentChats && vm.optionChat === 'student') {
                let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
                let response = await UserIndex.search('', { filters: filter, hitsPerPage: 1000 })
                vm.options = response.hits
            } else if (vm.chatType === 'group' || vm.optionChat === 'group') {
                vm.options = vm.groups
            } else {
                vm.options = vm.teams
            }
        },
        setTeam(team) {
            const vm = this
            vm.team = team?.groups ? team : vm.teams.find(t => t.name?.toLowerCase() === team?.toLowerCase()) || null
            vm.subOption = []
        },
        keyOf(item) {
            return item.objectID || item.id
        },
        isChecked(item, arr) {
            const vm = this
            return arr.some(el => vm.keyOf(el) === vm.keyOf(item))
        },
        toggle(item, arr, event) {
            const vm = this
            const idx = arr.findIndex(el => vm.keyOf(el) === vm.keyOf(item))
            if (idx >= 0) {
                arr.splice(idx, 1)
            } else {
                if (arr.length >= MAX_SELECTED) {
                    if (event) event.target.checked = false
                    return vm.toast(vm.$t('common.max_selection'), 'error')
                }
                arr.push(item)
            }
        },
        clearSelection() {
            const vm = this
            vm.option = []
            vm.subOption = []
            vm.team = null
            vm.search = ''
            vm.subSearch = ''
        },
        filter() {
            const vm = this
            const result = vm.isTeamChats ? vm.subOption : vm.option
            vm.$emit('filter', result.length ? result : null)
            vm.hide()
        }
    },
}
</script>

<style scoped>
.fc-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.fc-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.fc-sublabel {
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.07em;
}

.fc-list {
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    background: var(--v-field-base);
}

.fc-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 16px;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    cursor: pointer;
    border-bottom: 1px solid var(--v-outline-base);
    user-select: none;
    transition: background 0.1s;
}

.fc-item:last-child {
    border-bottom: none;
}

.fc-item:hover {
    background: rgba(51, 153, 153, 0.06);
}

.fc-item input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--v-accent-base);
    cursor: pointer;
    flex-shrink: 0;
}

.fc-item-name {
    flex: 1;
    min-width: 0;
}

.fc-item-count {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    padding: 1px 7px;
    flex-shrink: 0;
}

.fc-empty {
    padding: 20px;
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    font-style: italic;
}
</style>
